import { Injectable } from '@angular/core';
import { of } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { CrudService } from 'src/app/core/services/crud.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  assignAgentTicket,
  assignAgentTicketFailure,
  assignAgentTicketSuccess,
  fetchSupportTicketsDetail,
  fetchSupportTicketsDetailFailure,
  fetchSupportTicketsDetailSuccess,
  fetchSupportTicketsList,
  fetchSupportTicketsListFailure,
  fetchSupportTicketsListSuccess,
  fetchSupportTicketsReplays,
  fetchSupportTicketsReplaysFailure,
  fetchSupportTicketsReplaysSuccess,
  ticketCards,
  ticketCardsFailure,
  ticketCardsSuccess,
  updateTicketStatus,
  updateTicketStatusFailure,
  updateTicketStatusSuccess,
  sendSupportTicketReply,
  sendSupportTicketReplySuccess,
  sendSupportTicketReplyFailure,
} from './support-tickets.actions';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';

@Injectable()
export class SupportTicketsEffects {
  constructor(
    private actions$: Actions,
    private CrudService: CrudService,
    private snackBar: MatSnackBar,
    private router: Router,
    private store: Store
  ) {}

  fetchData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchSupportTicketsList),
      mergeMap((action: any) =>
        this.CrudService.fetchData(
          `Admin/GetTickets?PageNumber=${action.payload.pageNumber}&PageSize=${action.payload.pageSize}
                &SortColumn=${action.payload.sortColumn}&SortOrder=${action.payload.sortOrder}&Search=${action.payload.search}`
        ).pipe(
          map((result) => fetchSupportTicketsListSuccess({ response: result })),

          catchError((error) => of(fetchSupportTicketsListFailure({ error })))
        )
      )
    )
  );

  fetchDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchSupportTicketsDetail),
      mergeMap((action) =>
        this.CrudService.getData('Admin/GetTicketById?id=' + action.payload.id).pipe(
          map((result) =>
            fetchSupportTicketsDetailSuccess({ response: result })
          ),

          catchError((error) => of(fetchSupportTicketsDetailFailure({ error })))
        )
      )
    )
  );

  assignAgentTicket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(assignAgentTicket),
      mergeMap((action) =>
        this.CrudService.patchData(
          `Admin/AssignTicketToAgent?ticketId=${action.payload.id}&agentUserId=${action.payload.agentId}`,
          {}
        ).pipe(
          tap((result) => {
            if (result.successful) {
              this.snackBar.open('Agent assign to ticket successfully', '', {
                duration: 3000,
                panelClass: 'success',
              });
            } else {
              this.snackBar.open(result.message, '', {
                duration: 3000,
                panelClass: 'danger',
              });
            }
          }),
          mergeMap((res) => [
            assignAgentTicketSuccess({ response: res.result }),
            fetchSupportTicketsList({
              payload: {
                pageNumber: 1,
                pageSize: 10,
                sortColumn: 'id',
                sortOrder: 'z',
                search: '',
              },
            }),
          ]),
          catchError((error) => {
            return of(assignAgentTicketFailure({ error }));
          })
        )
      )
    )
  );

  updateTicketStatus$ = createEffect(() =>
      this.actions$.pipe(
        ofType(updateTicketStatus),
        mergeMap((action) =>
          this.CrudService.putData(`Admin/UpdateTicketStatus?id=${action.payload.id}`, {status: action.payload.status}).pipe(
            tap((result) => {
              if (result.successful) {
                this.snackBar.open('Ticket Status Updated successfully', '', {
                  duration: 3000,
                  panelClass: 'success',
                });
                this.router.navigate(['/tickets/list'])
              } else {
                this.snackBar.open(result.message, '', {
                  duration: 3000,
                  panelClass: 'danger',
                });
              }
            }),
            map((res) => updateTicketStatusSuccess({ response: res.result })),
            catchError((error) => {
              return of(updateTicketStatusFailure({ error }));
            })
          )
        )
      )
    );

    fetchTicketCards$ = createEffect(() =>
      this.actions$.pipe(
        ofType(ticketCards),
        mergeMap((action: any) =>
          this.CrudService.fetchData('Admin/ticket-status-counts').pipe(
            map((result) => ticketCardsSuccess({ response: result })),
            catchError((error) => of(ticketCardsFailure({ error })))
          )
        )
      )
    );

    fetchTicketReplays$ = createEffect(() =>
      this.actions$.pipe(
        ofType(fetchSupportTicketsReplays),
        mergeMap((action: any) =>
          this.CrudService.fetchData(
            `Ticket/GetReplies?ticketId=${action.ticketId.ticketId}`
          ).pipe(
            map((result) => fetchSupportTicketsReplaysSuccess({ response: result })),

            catchError((error) => of(fetchSupportTicketsReplaysFailure({ error })))
          )
        )
      )
    );

  sendTicketReply$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sendSupportTicketReply),
      mergeMap((action) =>
        this.CrudService.postFormData(
          'Ticket/AddReply',
          action.formData
        ).pipe(
          map((result) => sendSupportTicketReplySuccess({ response: result })),
          tap((result) => {
            // Refetch replies after sending
            const ticketId = action.formData.get('TicketId');
            if (ticketId) {
              this.store.dispatch(fetchSupportTicketsReplays({ ticketId: { ticketId: Number(ticketId) } }));
            }
          }),
          catchError((error) => of(sendSupportTicketReplyFailure({ error })))
        )
      )
    )
  );
}
